import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useProduct, { useProductForm } from '@hooks/useProduct'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'DuplicateProductModal'

type ModalCallbacks = {
  closeModal: () => void,
  duplicateProduct: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const DuplicateProductModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, duplicateProduct } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const product = digObject(modalPayload, 'product', {})

  const {
    callbacks: {
      duplicateProduct: duplicateFn,
    },
    creating,
  } = useProduct(product)

  const productFormPayload = useProductForm(
    { ...product, name: `${product.name} - Copy` },
    { validateOn: 'change' },
  )

  const {
    entityState,
    entityState: {
      name,
    },
    errors,
    handlers,
    saveEnabled,
  } = productFormPayload

  // To be used with handleCallbackAction
  const duplicateProductPayload = {
    callbacks: {
      action: duplicateFn,
      afterAction: closeModal,
    },
    shouldRedirect: true,
    entityParams: entityState,
    toastText: 'Product Duplicated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Duplicate" titleSecondLine="Product" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Name" errorMessage={errors.name}>
            <input name="name" type="text" value={name} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          loadingBubbleColor="white"
          onClick={() => duplicateProduct(duplicateProductPayload)}
          size="large"
        >
          {saveEnabled ? 'Duplicate Product' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <DuplicateProductModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
